import angular from 'angular';
import csmCommonWeb from 'csm-common-web';

export default angular
  .module('books.util.DGProductPurchase', [csmCommonWeb])
  .factory('DGProductPurchase', DGProductPurchaseFactory).name;

/*****************************************************************************
 * Constructor Factory: DGProductPurchase
 *
 * Returns a constructor function to create objects to manage product
 * purchasing. This will be a wrapper for a <b>ProductPurchase</b> item.
 *****************************************************************************/
/* @ngInject */
function DGProductPurchaseFactory(SERVER, Tools, InventoryOptionsColumn) {
  /*****************************************************************************
   * Class: DGProductPurchase
   *
   * Datagrid item class. This will manage a child ProductPurchase object. The
   * provided src can be a ProductPurchase or an InventoryProduct item. When an
   * inventory product item is used, the product should be set to set the memo
   * correctly.
   *
   * @param src         Source object to populate properties
   * @param isInventory Indication source is an InventoryProduct
   * @param product     Product item object
   *****************************************************************************/
  function DGProductPurchase(src, isInventory, product) {
    /** Ledger Item to manage. */ var mLedgerItem = null;
    /** Freight Amount. */ var mFreight = 0;
    /** Optional Inventory Options */ var mOptions = [];
    /** Optional Product Name. */ var mProductName = product
      ? product.name
      : '';

    /*****************************************************************************
     * hasOptions */
    /**
     * Returns true if this item came from a product with multiple options, false
     * otherwise.
     *
     * @return  Boolean
     *****************************************************************************/
    this.hasOptions = function () {
      return this.options.length > 0;
    };

    /** Accessor for the price field on the ledger item. */
    Object.defineProperty(this, 'cost', {
      get: function () {
        return mLedgerItem.price;
      },
      set: function (value) {
        if (isNaN(value) || value < 0) return;

        mLedgerItem.price = Tools.roundToPrecision(value, 2);
        recalc();
      }
    });

    /** Accessor for the freight field. */
    Object.defineProperty(this, 'freight', {
      get: function () {
        return mFreight;
      },
      set: function (value) {
        if (isNaN(value) || value < 0) return;

        mFreight = Number(value);
        recalc();
      }
    });

    /** Accessor for the itemID field on the ledger item. */
    Object.defineProperty(this, 'itemID', {
      get: function () {
        return mLedgerItem.itemID;
      },
      set: function (value) {
        mLedgerItem.itemID = value;
      }
    });

    /** Accessor for the memo field on the ledger item. */
    Object.defineProperty(this, 'memo', {
      set: function () {} /* Does nothing, needed for editing with UI-Grid  */,
      get: function () {
        return mLedgerItem.memo;
      }
    });

    /** Accessor for the options field. When set, the memo is updated. */
    Object.defineProperty(this, 'options', {
      get: function () {
        return mOptions;
      },
      set: function (value) {
        mOptions = value;
        mLedgerItem.memo =
          mProductName +
          (mOptions
            ? ' ' + InventoryOptionsColumn.optionsLabelFunction(mOptions)
            : '');
      }
    });

    /** Accessor for the quantity field on the ledger item. */
    Object.defineProperty(this, 'qty', {
      get: function () {
        return mLedgerItem.quantity;
      },
      set: function (value) {
        if (isNaN(value) || value < 0) return;

        mLedgerItem.quantity = Math.ceil(Number(value));
        recalc();
      }
    });

    /** Accessor for the subTotal field on the ledger item. */
    Object.defineProperty(this, 'subTotal', {
      get: function () {
        return mLedgerItem.subTotal;
      }
    });

    /** Accessor for the tax field on the ledger item. */
    Object.defineProperty(this, 'tax', {
      get: function () {
        return mLedgerItem.tax;
      },
      set: function (value) {
        if (isNaN(value) || value < 0) return;

        mLedgerItem.tax = Tools.roundToPrecision(Number(value), 2);
        mLedgerItem.taxRate =
          mLedgerItem.subTotal != 0
            ? Tools.roundToPrecision(mLedgerItem.tax / mLedgerItem.subTotal, 4)
            : 0;
        mLedgerItem.taxable = mLedgerItem.taxRate > 0;
        recalc(true);
      }
    });

    /** Accessor for the amount field on the ledger item. */
    Object.defineProperty(this, 'total', {
      get: function () {
        return mLedgerItem.amount;
      }
    });

    /** Accessor for source ledger item. */
    this.getSource = function () {
      return mLedgerItem;
    };

    /*------------------------------------------------*/
    /* Set the ledger item source. If this is an      */
    /* inventory item, a new item needs to be created */
    /*------------------------------------------------*/
    if (isInventory) {
      mLedgerItem = {
        type: SERVER.BooksDBConstants.LIT_PRODUCT_PURCHASE,
        itemID: product.id,
        price: src.averageCost,
        quantity: 0,
        subTotal: 0,
        tax: 0,
        taxRate: 0,
        amount: 0,
        inventoryProductID: src.id
      };
      this.options = src.options;
    } else mLedgerItem = src || {};

    /***************************************************************************
     * recalc */
    /**
     * Update total properties on the source item.
     ***************************************************************************/
    var recalc = function (ignoreTax) {
      mLedgerItem.subTotal = Tools.roundToPrecision(
        mLedgerItem.quantity * mLedgerItem.price,
        2
      );

      if (ignoreTax)
        mLedgerItem.tax = Tools.roundToPrecision(
          mLedgerItem.taxRate * mLedgerItem.subTotal,
          2
        );
      mLedgerItem.amount = Tools.roundToPrecision(
        mLedgerItem.subTotal + mLedgerItem.tax + mFreight,
        2
      );
    };
  }

  return DGProductPurchase;
}
